import { ArrowLeftIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'

import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import {
  SideNav,
  SideNavContent,
  SideNavHeaderSkeleton,
  SideNavLayout,
  useSideNavTab,
} from '@/components/layout/side-nav'
import { Collapse } from '@/components/ui/collapse'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { getPathString } from '@/factories/create-router-factory'
import { useCaptureEvent, useDesignVersionToggle } from '@/hooks'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { UserRoleEnum } from '@/models/features/user.models'
import { selectBuildingStrict, selectBuildingsLoaded } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import {
  COLLAPSE_STYLE,
  getPlaceNavGroups,
  getPlaceNavItems,
  PLACE_FEATURES_VIEW_KEY,
} from './place-details.constants'
import { PlaceFeaturesView, PlaceFeaturesViewSchema, PlaceTab } from './place-details.types'
import { resolveActiveTab } from './place-details.utils'
import { PlaceNavHeader } from './place-nav-header'
import { PlaceTabSkeleton } from './place-tab-skeleton'
import { AccessControlTab } from './tabs/access-control'
import { CredentialsTab } from './tabs/credentials'
import { FeaturesTab } from './tabs/features'
import { GeneralTab } from './tabs/general'
import { LanguagesTab } from './tabs/languages'
import { OtherTab } from './tabs/other'
import { PerksTab } from './tabs/perks'
import { PmsTab } from './tabs/pms'

export const PlaceDetailsPage = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const { buildingId } = useParams<{ buildingId: string }>()

  const building = useSelector(selectBuildingStrict)(buildingId)
  const buildingsLoaded = useSelector(selectBuildingsLoaded)
  const isAdmin = useSelector(selectUserRole) === UserRoleEnum.enum.Administrator

  const {
    canToggle,
    redirect,
    toggle: handleUIToggle,
  } = useDesignVersionToggle({
    group: 'places',
    screen: 'PlaceDetails',
    to: {
      params: { buildingId: buildingId ?? '' },
      path: '/dashboard/organisation/places/legacy/:buildingId',
    },
    version: 'new',
  })

  const { captureEvent } = useCaptureEvent()
  const location = useLocation()

  // Note: layout of the Features and Access control tabs - the current cards or the collapsible groups
  const [storedFeaturesView, setStoredFeaturesView] = useLocalStorage<string>(
    PLACE_FEATURES_VIEW_KEY,
    PlaceFeaturesView.classic,
  )
  const featuresView = PlaceFeaturesViewSchema.catch(PlaceFeaturesView.classic).parse(
    storedFeaturesView,
  )

  const perksAvailable = building ? !!building.perksAvailable : true
  const { activeTab, handleDirtyChange, handleTabChange } = useSideNavTab((rawTab) =>
    resolveActiveTab(rawTab, perksAvailable, isAdmin),
  )

  const isGroupedViewTab = activeTab === PlaceTab.features || activeTab === PlaceTab.accessControl

  const handleFeaturesViewToggle = () => {
    const nextView =
      featuresView === PlaceFeaturesView.grouped
        ? PlaceFeaturesView.classic
        : PlaceFeaturesView.grouped

    captureEvent('user_action', {
      from_path: location.pathname,
      from_ui_version: featuresView,
      name: 'design_toggle',
      screen:
        activeTab === PlaceTab.accessControl ? 'PlaceDetailsAccessControl' : 'PlaceDetailsFeatures',
      to_path: location.pathname,
      to_ui_version: nextView,
    })

    setStoredFeaturesView(nextView)
  }

  const getTabHref = (tab: PlaceTab) =>
    getPathString({
      params: { buildingId: buildingId ?? '' },
      path: '/dashboard/organisation/places/:buildingId',
      queryParams: { tab },
    })

  if (redirect) return redirect

  const backButton = (
    <Link navigateBack size="small" use="secondary" to={{ path: '/dashboard/organisation/places' }}>
      <Icon icon={ArrowLeftIcon} size="1.4rem" />
      {t('common:actions.back')}
    </Link>
  )

  if (buildingsLoaded && !building) {
    return (
      <Page centered>
        <PageHeader backButton={backButton} title={t('organisation:places.details.title')} />
        <EmptyState
          actions={backButton}
          description={t('organisation:places.details.notFound.description')}
          title={t('organisation:places.details.notFound.title')}
        />
      </Page>
    )
  }

  return (
    <Page centered fillHeight={activeTab === PlaceTab.perks}>
      <PageHeader
        actions={
          <>
            {canToggle && isGroupedViewTab && (
              <ToggleSwitch
                checked={featuresView === PlaceFeaturesView.grouped}
                falsyLabel={t('organisation:places.features.view.classic')}
                truthyLabel={t('organisation:places.features.view.grouped')}
                onChange={handleFeaturesViewToggle}
              />
            )}
            {canToggle && (
              <ToggleSwitch
                checked={true}
                falsyLabel={t('organisation:places.table.designToggle.old')}
                truthyLabel={t('organisation:places.table.designToggle.new')}
                onChange={handleUIToggle}
              />
            )}
          </>
        }
        backButton={backButton}
        title={t('organisation:places.details.title')}
      />

      <SideNavLayout>
        <SideNav
          activeTab={activeTab}
          getHref={getTabHref}
          groups={getPlaceNavGroups(t)}
          header={building ? <PlaceNavHeader building={building} /> : <SideNavHeaderSkeleton />}
          items={getPlaceNavItems(t, { isAdmin, perksAvailable })}
          onTabChange={handleTabChange}
        />

        <SideNavContent key={`${buildingId}-${activeTab}`} scroll={activeTab === PlaceTab.perks}>
          {activeTab === PlaceTab.perks ? (
            <PerksTab building={building ?? undefined} buildingId={Number(buildingId)} />
          ) : activeTab === PlaceTab.credentials ? (
            <Collapse in animation="collapse" style={COLLAPSE_STYLE}>
              <CredentialsTab
                buildingId={Number(buildingId)}
                isAdmin={isAdmin}
                onDirtyChange={handleDirtyChange}
              />
            </Collapse>
          ) : !building ? (
            <PlaceTabSkeleton activeTab={activeTab} featuresView={featuresView} isAdmin={isAdmin} />
          ) : (
            <Collapse in animation="collapse" style={COLLAPSE_STYLE}>
              {activeTab === PlaceTab.general && (
                <GeneralTab
                  building={building}
                  isAdmin={isAdmin}
                  onDirtyChange={handleDirtyChange}
                />
              )}
              {activeTab === PlaceTab.features && (
                <FeaturesTab
                  building={building}
                  isAdmin={isAdmin}
                  view={featuresView}
                  onDirtyChange={handleDirtyChange}
                />
              )}
              {activeTab === PlaceTab.languages && isAdmin && (
                <LanguagesTab building={building} onDirtyChange={handleDirtyChange} />
              )}
              {activeTab === PlaceTab.pms && (
                <PmsTab building={building} isAdmin={isAdmin} onDirtyChange={handleDirtyChange} />
              )}
              {activeTab === PlaceTab.accessControl && (
                <AccessControlTab
                  building={building}
                  isAdmin={isAdmin}
                  view={featuresView}
                  onDirtyChange={handleDirtyChange}
                />
              )}
              {activeTab === PlaceTab.other && (
                <OtherTab building={building} isAdmin={isAdmin} onDirtyChange={handleDirtyChange} />
              )}
            </Collapse>
          )}
        </SideNavContent>
      </SideNavLayout>
    </Page>
  )
}
